import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export type LinkStatus = 'idle' | 'checking' | 'online' | 'offline'

export interface ConnectionState {
  cloudUrl: string
  adminKey: string
  /** True once the operator has supplied both values at least once. */
  configured: boolean
  status: LinkStatus
  lastError: string | null
  lastSyncedAt: string | null
}

// Deployments can bake in their own default; operators can still change it in
// Settings, and a stored value always wins over this.
const DEFAULT_CLOUD_URL =
  import.meta.env.VITE_DEFAULT_CLOUD_URL || 'https://central-server-jned.onrender.com/api/v2'
const STORAGE_URL = 'admin.cloudUrl'
const STORAGE_KEY = 'admin.adminKey'

function read(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback
  } catch {
    return fallback
  }
}

// Migrate the keys the single-file console used, so an existing operator keeps
// their settings on first open of the new portal.
function initialConfig(): { cloudUrl: string; adminKey: string } {
  const cloudUrl = read(STORAGE_URL, read('admin_cloud', DEFAULT_CLOUD_URL))
  const adminKey = read(STORAGE_KEY, read('admin_key', ''))
  return { cloudUrl: cloudUrl.replace(/\/+$/, ''), adminKey }
}

const seed = initialConfig()

const initialState: ConnectionState = {
  cloudUrl: seed.cloudUrl,
  adminKey: seed.adminKey,
  configured: Boolean(seed.cloudUrl && seed.adminKey),
  status: 'idle',
  lastError: null,
  lastSyncedAt: null
}

const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    credentialsSaved(state, action: PayloadAction<{ cloudUrl: string; adminKey: string }>) {
      state.cloudUrl = action.payload.cloudUrl.trim().replace(/\/+$/, '')
      state.adminKey = action.payload.adminKey.trim()
      state.configured = Boolean(state.cloudUrl && state.adminKey)
      state.status = 'checking'
      state.lastError = null
      try {
        localStorage.setItem(STORAGE_URL, state.cloudUrl)
        localStorage.setItem(STORAGE_KEY, state.adminKey)
      } catch {
        /* private mode — the session still works, it just will not be remembered */
      }
    },
    disconnected(state) {
      state.configured = false
      state.status = 'idle'
      state.adminKey = ''
      state.lastError = null
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch {
        /* ignore */
      }
    },
    linkChecking(state) {
      state.status = 'checking'
    },
    linkOnline(state) {
      state.status = 'online'
      state.lastError = null
      state.lastSyncedAt = new Date().toISOString()
    },
    linkOffline(state, action: PayloadAction<string>) {
      state.status = 'offline'
      state.lastError = action.payload
    }
  }
})

export const { credentialsSaved, disconnected, linkChecking, linkOnline, linkOffline } =
  connectionSlice.actions
export default connectionSlice.reducer
